import { useEffect } from "react";

/**
 * Custom hook that detects when the specified DOM node is removed
 * and dispatches a JSUnmountReactNode event to force an unmount
 *
 * This is required as React components mounted using `Strava.ReactComponentMounter.mountComponent` do not unmount automatically
 * when their parent DOM node is removed outside of React (e.g., via jQuery).
 *
 * @param node - The target HTMLDivElement to observe for removal (Note: must have an ID)
 */
export const useMutationObserverToForceUnmount = (node: HTMLDivElement) => {
  useEffect(() => {
    // Setup MutationObserver to observe DOM mutation
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          for (const removedNode of mutation.removedNodes) {
            // If the removedNode (or any of its children) is the target node
            if (
              removedNode instanceof Element &&
              removedNode.querySelector(`#${node.id}`)
            ) {
              // Dispatch custom `JSUnmountReactNode` event
              // The listener for this event is setup in `react-component-mounter.js`
              document.dispatchEvent(
                new CustomEvent("JSUnmountReactNode", {
                  detail: { node }
                })
              );

              // Stop observing since we've found and handled the removal
              observer.disconnect();
              return;
            }
          }
        }
      }
    });

    // Start observing the document body for changes to catch when our node gets removed
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup function to disconnect the observer when the component unmounts or node changes
    return () => {
      observer.disconnect();
    };
  }, [node]);
};
